<template>
    <div class="launch-anim flex flex-col items-center gap-7.5 pt-8 pb-2">
        <div class="la-stage" aria-hidden="true">
            <svg v-for="(tri, index) in TRIANGLES" :key="index" class="la-tri" viewBox="0 0 38 30" :style="triStyle(index)">
                <polygon points="19,1.23 36.77,28.77 1.23,28.77" :fill="tri.fill" :stroke="tri.fill" />
            </svg>
        </div>
        <p class="body-small text-valence-stone-500 animate-pulse motion-reduce:animate-none" role="status" aria-live="polite">{{ label }}</p>
    </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from "vue";

interface Props {
    label?: string;
}

const { label = "Launching your 360…" } = defineProps<Props>();

const emit = defineEmits<{ done: [] }>();

// Same pink pair as ReflectionIcon's `sixty` mark, which this animation resolves into.
const TRIANGLES = [{ fill: "#F6A6C5" }, { fill: "#E84C8D" }, { fill: "#F6A6C5" }, { fill: "#E84C8D" }] as const;
const RING_RADIUS = 37;
const SEPARATION = 24;
const LINE_SPACING = 30;
const LINE_STAGGER = 130;
const SEPARATE_AT = 0;
const BURST_AT = 340;
const LINE_AT = 1160;
const SNAP_AT = LINE_AT + (TRIANGLES.length - 1) * LINE_STAGGER + 400 + 460;
const DONE_AT = SNAP_AT + 440;

type Phase = "mark" | "separate" | "burst" | "line";

const phase = ref<Phase>("mark");
const revealed = ref(false);
const timers: ReturnType<typeof setTimeout>[] = [];

function markTransform(index: number): string {
    return `translate(0px, ${index % 2 === 0 ? -10 : 10}px)`;
}

function triStyle(index: number): Record<string, string> {
    const opacity = index > 1 && !revealed.value ? "0" : "1";
    switch (phase.value) {
        case "mark":
            return { transform: markTransform(index), opacity, transition: "transform .38s cubic-bezier(.25,1.28,.5,1), opacity .3s ease" };
        case "separate":
            return {
                transform: index > 1 ? markTransform(index) : `translate(0px, ${index === 0 ? -SEPARATION : SEPARATION}px)`,
                opacity,
                transition: "transform .28s cubic-bezier(.4,0,.2,1), opacity .3s ease",
            };
        case "burst": {
            const degrees = -90 + index * (360 / TRIANGLES.length);
            const radians = (degrees * Math.PI) / 180;
            const x = (Math.cos(radians) * RING_RADIUS).toFixed(1);
            const y = (Math.sin(radians) * RING_RADIUS).toFixed(1);
            return {
                transform: `translate(${x}px, ${y}px) rotate(${degrees + 90}deg)`,
                opacity,
                transition: "transform 600ms cubic-bezier(.16,.8,.3,1), opacity .35s ease",
            };
        }
        case "line": {
            const y = ((index - (TRIANGLES.length - 1) / 2) * LINE_SPACING).toFixed(1);
            return {
                transform: `translate(0px, ${y}px)`,
                opacity,
                transition: "transform .4s cubic-bezier(.3,.85,.3,1), opacity .3s ease",
                transitionDelay: `${index * LINE_STAGGER}ms`,
            };
        }
    }
}

function at(delay: number, run: () => void) {
    timers.push(setTimeout(run, delay));
}

onMounted(() => {
    if (globalThis.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        at(450, () => emit("done"));
        return;
    }
    at(SEPARATE_AT, () => (phase.value = "separate"));
    at(BURST_AT, () => {
        revealed.value = true;
        phase.value = "burst";
    });
    at(LINE_AT, () => (phase.value = "line"));
    at(SNAP_AT, () => (phase.value = "mark"));
    at(DONE_AT, () => emit("done"));
});

onBeforeUnmount(() => timers.forEach(clearTimeout));
</script>

<style scoped>
@reference "../../../../css/tailwind.css";

.la-stage {
    position: relative;
    width: 180px;
    height: 180px;
}

.la-tri {
    position: absolute;
    left: 50%;
    top: 50%;
    width: 38px;
    height: 30px;
    margin: -15px 0 0 -19px;
    overflow: visible;
    will-change: transform, opacity;
}

.la-tri polygon {
    stroke-width: 2.46px;
    stroke-linejoin: round;
}
</style>
